import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { CreditCard, Phone, CheckCircle, XCircle, Clock, AlertCircle, DollarSign, Upload } from 'lucide-react';

const Payment = () => {
  const { unitId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [unitName, setUnitName] = useState('Unit'); // Default name, will be updated if available
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState('0');
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkoutRequestId, setCheckoutRequestId] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [studentData, setStudentData] = useState(null);

  // Cash payment fields
  const [cashPaymentNotes, setCashPaymentNotes] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [receiptImage, setReceiptImage] = useState(null);
  const [receiptPreview, setReceiptPreview] = useState(null);

  useEffect(() => {
    fetchStudentData();
  }, []);

  useEffect(() => {
    if (studentData?.campusId) {
      fetchUnitCost();
    }
  }, [unitId, studentData]);

  useEffect(() => {
    // Cleanup polling interval on unmount
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  const fetchStudentData = async () => {
    try {
      const response = await axios.get(`/students/${user.id}?includeCourse=true&includeCampus=true`);
      console.log('Student data response:', response.data);
      console.log('Student courseId:', response.data.courseId);
      console.log('Student course object:', response.data.course);
      setStudentData(response.data);
    } catch (error) {
      console.error('Error fetching student data:', error);
    }
  };

  const fetchUnitCost = async () => {
    try {
      console.log('Fetching unit cost for unit:', unitId, 'campus:', studentData.campusId);

      // Fetch current cost for this unit and student's campus (like mobile app)
      const costResponse = await axios.get(`/unit-costs/current/${unitId}/${studentData.campusId}`);
      console.log('Unit cost response:', costResponse.data);

      if (costResponse.data) {
        setAmount(costResponse.data.cost.toString());
      } else {
        // If no pricing found, show error
        alert('Pricing Error: No pricing information available for this unit. Please contact your administrator.');
        setAmount('0');
      }
    } catch (error) {
      console.error('Error fetching unit cost:', error);
      alert('Failed to load pricing information');
    }
  };

  const formatPhoneNumber = (phone) => {
    // Remove any spaces, dashes, or brackets
    let cleaned = phone.replace(/[\s\-\(\)]/g, '');

    // If it starts with 0, replace with 254
    if (cleaned.startsWith('0')) {
      cleaned = '254' + cleaned.substring(1);
    }

    // If it doesn't start with 254 or +, add 254
    if (!cleaned.startsWith('254') && !cleaned.startsWith('+')) {
      cleaned = '254' + cleaned;
    }

    // Remove + if present
    cleaned = cleaned.replace(/^\+/, '');

    return cleaned;
  };

  const validatePhoneNumber = (phone) => {
    const kenyaPhoneRegex = /^254[0-9]{9}$/;
    return kenyaPhoneRegex.test(phone);
  };

  const initiatePayment = async () => {
    const formattedPhone = formatPhoneNumber(phoneNumber);

    if (!validatePhoneNumber(formattedPhone)) {
      alert('Please enter a valid Kenyan phone number (e.g., 0712345678 or 254712345678)');
      return;
    }

    if (!amount || parseFloat(amount) <= 0) {
      alert('Invalid amount');
      return;
    }

    try {
      setLoading(true);
      setPaymentStatus('initiating');

      const response = await axios.post('/payments/initiate-mpesa', {
        studentId: user.id,
        unitId: parseInt(unitId),
        phoneNumber: formattedPhone,
        amount: parseFloat(amount)
      });

      if (response.data.success) {
        setCheckoutRequestId(response.data.checkoutRequestId);
        setPaymentStatus('pending');

        alert(response.data.customerMessage || 'Please check your phone and complete the payment.');

        // Start polling for payment status
        startPaymentPolling(response.data.checkoutRequestId);
      } else {
        setPaymentStatus('failed');
        alert(response.data.message || 'Failed to initiate payment');
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      setPaymentStatus('failed');
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const startPaymentPolling = (requestId) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`/payments/status/${requestId}`);

        if (response.data.success) {
          if (response.data.status === 'confirmed') {
            setPaymentStatus('confirmed');
            clearInterval(interval);
            setPollingInterval(null);

            alert('Payment successful! You can now access your enrolled unit.');
            navigate('/units');
          }
          // Continue polling if still pending
        } else {
          // Stop polling on error
          clearInterval(interval);
          setPollingInterval(null);
          setPaymentStatus('failed');
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        clearInterval(interval);
        setPollingInterval(null);
        setPaymentStatus('failed');
      }
    }, 5000); // Check every 5 seconds

    setPollingInterval(interval);

    // Stop polling after 5 minutes
    setTimeout(() => {
      if (interval) {
        clearInterval(interval);
        setPollingInterval(null);
        if (paymentStatus === 'pending') {
          setPaymentStatus('timeout');
          alert('Payment verification timed out. Please check your M-Pesa messages or contact support.');
        }
      }
    }, 300000); // 5 minutes
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'text-green-600 bg-green-100';
      case 'pending':
      case 'initiating':
        return 'text-orange-600 bg-orange-100';
      case 'failed':
      case 'timeout':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':
        return <CheckCircle className="w-5 h-5" />;
      case 'pending':
      case 'initiating':
        return <Clock className="w-5 h-5" />;
      case 'failed':
      case 'timeout':
        return <XCircle className="w-5 h-5" />;
      default:
        return <CreditCard className="w-5 h-5" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed':
        return 'Payment Confirmed';
      case 'pending':
        return 'Waiting for Payment...';
      case 'initiating':
        return 'Initiating Payment...';
      case 'failed':
        return 'Payment Failed';
      case 'timeout':
        return 'Payment Timeout';
      default:
        return 'Ready to Pay';
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setReceiptImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => setReceiptPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const initiateCashPayment = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      alert('Invalid amount');
      return;
    }

    try {
      setLoading(true);
      setPaymentStatus('submitting');

      // Convert image to base64 if provided
      let receiptImageBase64 = null;
      if (receiptImage) {
        receiptImageBase64 = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.readAsDataURL(receiptImage);
        });
      }

      const response = await axios.post('/payments/initiate-cash', {
        studentId: user.id,
        unitId: parseInt(unitId),
        amount: parseFloat(amount),
        paymentNotes: cashPaymentNotes.trim(),
        referenceNumber: referenceNumber.trim(),
        receiptImage: receiptImageBase64
      });

      if (response.data.success) {
        setPaymentStatus('pending-approval');
        alert('Cash payment request submitted successfully! Waiting for lecturer approval.');
      } else {
        setPaymentStatus('failed');
        alert(response.data.message || 'Failed to submit cash payment request');
      }
    } catch (error) {
      console.error('Error submitting cash payment:', error);
      setPaymentStatus('failed');
      alert('Failed to submit cash payment request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900">Payment</h1>
        <p className="text-gray-600 mt-2">
          Complete payment for {unitName || 'your unit'}
        </p>
      </div>

      {/* Payment Status */}
      {paymentStatus && (
        <div className="max-w-md mx-auto">
          <div className={`flex items-center space-x-3 p-4 rounded-lg ${getStatusColor(paymentStatus)}`}>
            {getStatusIcon(paymentStatus)}
            <span className="font-medium">{getStatusText(paymentStatus)}</span>
          </div>
        </div>
      )}

      {/* Payment Method Selection */}
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Choose Payment Method</h2>

          <div className="grid grid-cols-2 gap-3 mb-6">
            <button
              onClick={() => setPaymentMethod('mpesa')}
              className={`p-4 border rounded-lg text-center transition-colors ${
                paymentMethod === 'mpesa'
                  ? 'border-blue-500 bg-blue-50 text-blue-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              disabled={paymentStatus === 'pending' || paymentStatus === 'confirmed' || paymentStatus === 'pending-approval'}
            >
              <CreditCard className="w-6 h-6 mx-auto mb-2 text-blue-600" />
              <span className="text-sm font-medium">M-Pesa</span>
            </button>

            <button
              onClick={() => setPaymentMethod('cash')}
              className={`p-4 border rounded-lg text-center transition-colors ${
                paymentMethod === 'cash'
                  ? 'border-green-500 bg-green-50 text-green-700'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              disabled={paymentStatus === 'pending' || paymentStatus === 'confirmed' || paymentStatus === 'pending-approval'}
            >
              <DollarSign className="w-6 h-6 mx-auto mb-2 text-green-600" />
              <span className="text-sm font-medium">Cash Payment</span>
            </button>
          </div>

          {/* Amount Display */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (KES)
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              value={amount}
              readOnly
            />
            <p className="text-xs text-gray-500 mt-1">
              Unit enrollment fee
            </p>
          </div>

          {/* M-Pesa Payment Form */}
          {paymentMethod === 'mpesa' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="tel"
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0712345678 or 254712345678"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    disabled={paymentStatus === 'pending' || paymentStatus === 'confirmed'}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter your M-Pesa registered phone number
                </p>
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-blue-900 mb-2">How it works:</h3>
                <ol className="text-xs text-blue-800 space-y-1">
                  <li>1. Enter your phone number</li>
                  <li>2. Click "Pay with M-Pesa"</li>
                  <li>3. Check your phone for the payment prompt</li>
                  <li>4. Enter your M-Pesa PIN to complete payment</li>
                </ol>
              </div>
            </div>
          )}

          {/* Cash Payment Form */}
          {paymentMethod === 'cash' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reference Number (Optional)
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  placeholder="e.g., Receipt #12345"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  disabled={paymentStatus === 'pending-approval' || paymentStatus === 'confirmed'}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Add a reference number from your payment receipt
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Notes (Optional)
                </label>
                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  rows={3}
                  placeholder="Add any additional notes about your payment..."
                  value={cashPaymentNotes}
                  onChange={(e) => setCashPaymentNotes(e.target.value)}
                  disabled={paymentStatus === 'pending-approval' || paymentStatus === 'confirmed'}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Receipt Image (Optional)
                </label>
                <div className="space-y-3">
                  <input
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileSelect}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    disabled={paymentStatus === 'pending-approval' || paymentStatus === 'confirmed'}
                  />
                  {receiptPreview && (
                    <div className="relative">
                      <img
                        src={receiptPreview}
                        alt="Receipt preview"
                        className="w-full h-32 object-cover rounded-lg border"
                      />
                      <button
                        onClick={() => {
                          setReceiptImage(null);
                          setReceiptPreview(null);
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs"
                      >
                        ×
                      </button>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Upload a photo of your payment receipt (optional)
                </p>
              </div>

              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="text-sm font-medium text-green-900 mb-2">Cash Payment Process:</h3>
                <ol className="text-xs text-green-800 space-y-1">
                  <li>1. Pay the required amount in cash to your lecturer</li>
                  <li>2. Fill in payment details and upload receipt (optional)</li>
                  <li>3. Submit payment request</li>
                  <li>4. Wait for lecturer approval</li>
                  <li>5. Access your unit once approved</li>
                </ol>
              </div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 mt-6">
          {paymentStatus === 'confirmed' ? (
            <button
              onClick={() => navigate('/units')}
              className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 transition-colors"
            >
              Go to My Units
            </button>
          ) : paymentStatus === 'pending-approval' ? (
            <div className="space-y-3">
              <div className="w-full bg-orange-100 text-orange-800 py-3 px-4 rounded-lg text-center font-medium">
                Waiting for Lecturer Approval
              </div>
              <button
                onClick={() => navigate('/units')}
                className="w-full bg-white text-blue-600 py-3 px-4 rounded-lg font-medium border border-blue-600 hover:bg-blue-50 transition-colors"
              >
                Back to Units
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <button
                onClick={paymentMethod === 'mpesa' ? initiatePayment : initiateCashPayment}
                disabled={loading || paymentStatus === 'pending'}
                className={`w-full text-white py-3 px-4 rounded-lg font-medium transition-colors ${
                  paymentMethod === 'mpesa'
                    ? 'bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400'
                    : 'bg-green-600 hover:bg-green-700 disabled:bg-gray-400'
                } disabled:cursor-not-allowed`}
              >
                {loading ? (
                  <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Processing...</span>
                  </div>
                ) : paymentStatus === 'pending' ? (
                  'Processing Payment...'
                ) : paymentMethod === 'mpesa' ? (
                  'Pay with M-Pesa'
                ) : (
                  'Submit Cash Payment'
                )}
              </button>

              <button
                onClick={() => navigate('/units')}
                disabled={loading || paymentStatus === 'pending'}
                className="w-full bg-white text-blue-600 py-3 px-4 rounded-lg font-medium border border-blue-600 hover:bg-blue-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:border-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Payment;
